/**
 * Site- and row-specific tooltip copy for portfolio matrix metrics.
 */
import type { SiteEntity } from '../../types/site';
import type { SiteComposite } from '../../types/portfolioComposite';
import { effectivePhases } from '../projectIntent';
import type { PillarId } from '../portfolio/pillarRegistry';
import {
  PILLARS,
  isAdvisoryPillar,
  isDecisionApplicablePillar,
  reportsForPillar,
} from '../portfolio/pillarRegistry';
import { PORTFOLIO_CONFIG } from '../portfolio/portfolioConfig';
import { QUADRANT_DESCRIPTIONS, QUADRANT_LABELS } from '../portfolio/quadrant';
import { siteRegionLabel, siteTierLabel } from '../portfolio/portfolioViewHelpers';
import { formatCapacityMw, formatScore2Dec } from '../portfolio/format';
import type { CalculatedMetricExplanation } from './calculatedMetricExplanation';

function sourceReportSummary(pillar: SiteComposite['pillars'][PillarId]): string {
  if (pillar.missing) return 'no source reports';
  const ids = pillar.sourceReportIds;
  if (ids.length === 0) return pillar.rationale || 'structured audit inputs';
  return `${ids.length} report(s): ${ids.slice(0, 2).join(', ')}${ids.length > 2 ? '…' : ''}`;
}

export function explainPortfolioCapacity(
  site: SiteEntity,
  composite: SiteComposite,
): CalculatedMetricExplanation {
  const phases = effectivePhases(site.projectIntent);
  const phased = phases.totalCapacityMW;
  const intentMw = site.projectIntent?.powerRequirementMW;
  let source = 'none set';
  if (phased != null && phased > 0) source = `ultimate (final-phase cumulative) capacity ${formatCapacityMw(phased)} MW`;
  else if (intentMw != null && intentMw > 0) source = `projectIntent.powerRequirementMW = ${formatCapacityMw(intentMw)}`;
  else if (composite.capacityMW > 0) source = `stored composite capacity ${formatCapacityMw(composite.capacityMW)} MW`;

  return {
    label: 'Capacity (MW)',
    formula: 'Phased capacity totals first, then exact projectIntent.powerRequirementMW, then power bucket midpoint.',
    inputs: `${formatCapacityMw(composite.capacityMW)} MW from ${source}.`,
    improve: 'Set phased build-out or exact MW on Site Configuration → Project intent so portfolio and dashboard totals match.',
  };
}

export function explainPortfolioCapacityHeader(): CalculatedMetricExplanation {
  return {
    label: 'Capacity (MW)',
    formula: 'Phased capacity totals first, then exact projectIntent.powerRequirementMW, then power bucket midpoint.',
    inputs: 'Per-site MW in this column from composite after Recompute.',
    improve: 'Set phased build-out or exact MW on Site Configuration → Project intent.',
  };
}

export function explainPortfolioTier(site: SiteEntity): CalculatedMetricExplanation {
  const tier = siteTierLabel(site);
  const region = siteRegionLabel(site);
  return {
    label: 'Tier',
    formula: 'Manual site classification from site metadata (tier / cluster assignment). Not scored.',
    inputs: tier === '—' ? 'No tier set on this site.' : `Tier: ${tier}. Region: ${region}.`,
    improve: 'Set tier on the site record or cluster assignment when portfolio grouping matters.',
  };
}

export function explainPortfolioTierHeader(): CalculatedMetricExplanation {
  return {
    label: 'Tier',
    formula: 'Manual site classification from site metadata. Not part of the composite score.',
    inputs: 'Displayed per site from site record fields.',
    improve: 'Set tier on each site for portfolio filtering and grouping.',
  };
}

export function explainPortfolioPillar(
  pillarId: PillarId,
  composite: SiteComposite,
): CalculatedMetricExplanation {
  const def = PILLARS[pillarId];
  const p = composite.pillars[pillarId];
  const weightPct = (def.weight * 100).toFixed(0);
  const types = reportsForPillar(pillarId);
  const typeLabel = types.length > 0 ? types.join(', ') : def.structuredSource ?? 'audit';

  if (!isDecisionApplicablePillar(pillarId, p)) {
    const advisory = isAdvisoryPillar(pillarId);
    return {
      label: def.label,
      formula: advisory
        ? 'Optional advisory pillar. It is outside the current decision composite and readiness denominator.'
        : 'Available-later pillar. It enters the decision composite only after its source model is ready.',
      inputs: p.applicabilityReason
        ?? (advisory
          ? 'This optional analysis does not affect decision readiness.'
          : 'The prerequisite source model is not ready.'),
      improve: advisory
        ? `Run ${typeLabel} only when its advisory question is useful.`
        : p?.applicabilityReasonCode === 'financial-model-missing'
          || p?.applicabilityReasonCode === 'financial-model-incomplete'
          ? 'Complete and save the project financial model before running Financial analysis.'
          : 'Choose the equipment basis and add a valid BOM item before running Supply Chain Risk analysis.',
    };
  }

  if (p?.missing) {
    return {
      label: def.label,
      formula: `Benefit score 0–5 (higher = better). Weight ${weightPct}% in composite. Missing pillars are stamped worst-case (${PORTFOLIO_CONFIG.WORST_CASE_SCORE}) for display, but EXCLUDED from the composite aggregate (HARD_MISSING) so a missing pillar does not drag the score like a measured-bad one.`,
      inputs: `Score ${formatScore2Dec(p?.score)} — no ${typeLabel} source. ${p?.rationale ?? ''}`.trim(),
      improve: `Generate or link the ${typeLabel} report(s) mapped to this pillar, then Recompute portfolio.`,
    };
  }

  return {
    label: def.label,
    formula: `Weighted benefit score 0–5 from latest mapped report(s) or structured audit inputs. Pillar weight: ${weightPct}% of raw composite before data-confidence penalty.`,
    inputs: `Score ${formatScore2Dec(p?.score)} (confidence ${((p?.confidence ?? 0) * 100).toFixed(0)}%). Sources: ${sourceReportSummary(p)}. ${p?.rationale ?? ''}`.trim(),
    improve: p?.score != null && p.score < 3
      ? `Refresh ${typeLabel} with stronger citations and resolved QA blocks; re-run portfolio Recompute.`
      : `Keep ${typeLabel} current — stale reports reduce confidence and can trigger data penalties.`,
  };
}

export function explainPortfolioComposite(composite: SiteComposite): CalculatedMetricExplanation {
  const rawParts = Object.values(composite.pillars)
    .filter(p => !p.missing && isDecisionApplicablePillar(p.pillarId, p))
    .map(p => `${PILLARS[p.pillarId].label.split(' ')[0]} ${formatScore2Dec(p.score)}×${(p.weight * 100).toFixed(0)}%`);
  const penalty = composite.dataCompletenessPenalty;

  return {
    label: 'Benefit composite',
    formula: 'Weighted mean of the site-applicable decision-pillar benefit scores (0–5, higher = better site), minus data-confidence penalty. Decision band inverts this to 1–5 risk.',
    inputs: [
      `Final ${formatScore2Dec(composite.composite)} after penalty −${penalty.toFixed(2)}.`,
      rawParts.length > 0 ? `Pillars: ${rawParts.join('; ')}.` : 'All pillars missing or worst-case.',
      `${composite.sourceReportIds.length} source report(s). Tag ${composite.confidenceTag}.`,
    ].join(' '),
    improve: penalty > 0.05
      ? 'Close QA gaps (missing providers, dangling citations, reconciliation blocks) and add missing pillar reports, then Recompute.'
      : 'Raise low pillar scores by generating domain reports; Recompute portfolio after each major update.',
  };
}

export function explainPortfolioRisk(composite: SiteComposite): CalculatedMetricExplanation {
  return {
    label: 'Risk score',
    formula: `Inverted composite: ${PORTFOLIO_CONFIG.SCORE_MAX} − benefit composite (0–5). Higher = more blockers.`,
    inputs: `${formatScore2Dec(composite.riskScore)} from composite ${formatScore2Dec(composite.composite)}.`,
    improve: 'Same as composite — add pillar source reports and resolve data-confidence penalties.',
  };
}

export function explainPortfolioOpportunity(composite: SiteComposite): CalculatedMetricExplanation {
  const b = composite.opportunityBreakdown;
  const parts = Object.entries(b).map(([k, v]) => `${k} ${formatScore2Dec(v.score)}×${(v.weight * 100).toFixed(0)}%`);
  return {
    label: 'Opportunity score',
    formula: 'Weighted blend of capacity scale, market depth, strategic timing, interconnection upside, and expansion potential (0–5). Independent of risk composite.',
    inputs: [
      `Score ${formatScore2Dec(composite.opportunityScore)}.`,
      parts.length > 0 ? `Components: ${parts.join('; ')}.` : composite.opportunityRationale,
    ].join(' '),
    improve: 'Set phased capacity and timeline on project intent; add grid/market evidence in power and interconnection reports.',
  };
}

export function explainPortfolioQuadrant(composite: SiteComposite): CalculatedMetricExplanation {
  const lowRisk = composite.riskScore <= PORTFOLIO_CONFIG.QUADRANT_RISK_THRESHOLD;
  const highOpp = composite.opportunityScore >= PORTFOLIO_CONFIG.QUADRANT_OPPORTUNITY_THRESHOLD;
  return {
    label: 'Quadrant',
    formula: `2×2 from risk (≤${PORTFOLIO_CONFIG.QUADRANT_RISK_THRESHOLD} = low) and opportunity (≥${PORTFOLIO_CONFIG.QUADRANT_OPPORTUNITY_THRESHOLD} = high).`,
    inputs: `${QUADRANT_LABELS[composite.quadrant]} — risk ${formatScore2Dec(composite.riskScore)} (${lowRisk ? 'low' : 'high'}), opportunity ${formatScore2Dec(composite.opportunityScore)} (${highOpp ? 'high' : 'low'}).`,
    improve: QUADRANT_DESCRIPTIONS[composite.quadrant],
  };
}

export function explainPortfolioDecisionBand(composite: SiteComposite): CalculatedMetricExplanation {
  return {
    label: 'Decision band',
    formula: 'Maps benefit composite (0–5) to GO / CONDITIONAL / CONDITIONAL HIGH / NO-GO via company decision-band thresholds.',
    inputs: `${composite.decisionBandLabel} — composite ${formatScore2Dec(composite.composite)}, decision score ${formatScore2Dec(composite.decisionScore)}. ${composite.decisionRationale}`,
    improve: composite.decisionBand === 'noGo' || composite.decisionBand === 'conditionalHigh'
      ? 'Focus on lowest pillar scores and missing reports shown in Audit; recompute after mitigation.'
      : 'Maintain evidence quality and refresh domain reports before diligence milestones.',
  };
}

export function explainPortfolioConfidence(composite: SiteComposite): CalculatedMetricExplanation {
  const b = composite.dataConfidenceBreakdown;
  const tag =
    composite.confidenceTag === 'V' ? 'Verified — strong evidence'
    : composite.confidenceTag === 'R' ? 'Reference — partial evidence'
    : 'Caution — thin or conflicting data';
  return {
    label: 'Data confidence',
    formula: 'QA penalty from missing providers, dangling citations, provenance gaps, distribution outliers, and reconciliation blocks. Subtracts from benefit composite.',
    inputs: [
      `Tag ${composite.confidenceTag} (${tag}). Penalty ${composite.dataCompletenessPenalty.toFixed(2)}.`,
      `Missing required ${b.missingRequiredProviders}, recommended ${b.missingRecommendedProviders}, dangling ${b.danglingCitations}, provenance ${b.provenanceGaps}, outliers ${b.distributionOutliers}, blocks ${b.reconciliationBlocks}.`,
    ].join(' '),
    improve: 'Upload cited source documents, resolve QA blocks in Audit, and satisfy required data providers per report type.',
  };
}

export function explainPortfolioCompositeHeader(): CalculatedMetricExplanation {
  return {
    label: 'Benefit composite',
    formula: 'Weighted mean of ten pillar benefit scores (0–5, higher = better), minus data-confidence penalty.',
    inputs: 'Per-site values in this column; hover a cell for pillar breakdown.',
    improve: 'Generate missing domain reports and Recompute portfolio on Dashboard.',
  };
}

export function explainPortfolioRiskHeader(): CalculatedMetricExplanation {
  return {
    label: 'Risk score',
    formula: '5 − benefit composite. Higher = more blockers.',
    inputs: 'Derived from the composite column on each row.',
    improve: 'Improve pillar scores and evidence quality on the site.',
  };
}

export function explainPortfolioOpportunityHeader(): CalculatedMetricExplanation {
  return {
    label: 'Opportunity score',
    formula: 'Capacity, market, timing, interconnection upside, and expansion — weighted 0–5.',
    inputs: 'Uses project intent MW/timeline and report narrative signals.',
    improve: 'Set phased capacity and refresh power / grid reports.',
  };
}

export function explainPortfolioQuadrantHeader(): CalculatedMetricExplanation {
  return {
    label: 'Quadrant',
    formula: `Risk ≤ ${PORTFOLIO_CONFIG.QUADRANT_RISK_THRESHOLD} and opportunity ≥ ${PORTFOLIO_CONFIG.QUADRANT_OPPORTUNITY_THRESHOLD} define the four quadrants.`,
    inputs: 'Each site plotted from its risk and opportunity scores.',
    improve: 'See cell tooltips for site-specific quadrant rationale.',
  };
}

export function explainPortfolioBandHeader(): CalculatedMetricExplanation {
  return {
    label: 'Decision band',
    formula: 'GO / CONDITIONAL / NO-GO from benefit composite vs calibrated thresholds.',
    inputs: 'Company decision-band config from portfolio settings.',
    improve: 'Adjust bands in admin only after calibration review.',
  };
}

export function explainPortfolioConfHeader(): CalculatedMetricExplanation {
  return {
    label: 'Confidence tag',
    formula: 'V = Verified, R = Reference, C = Caution — from aggregate QA penalty on site reports.',
    inputs: 'Penalty subtracted from composite during Recompute.',
    improve: 'Resolve Audit QA findings and upload missing cited documents.',
  };
}

export function explainPortfolioPillarHeader(pillarId: PillarId): CalculatedMetricExplanation {
  const def = PILLARS[pillarId];
  const types = reportsForPillar(pillarId);
  return {
    label: def.label,
    formula: `Benefit score 0–5 from ${types.length > 0 ? types.join(' + ') : def.structuredSource ?? 'audit'}. Weight ${(def.weight * 100).toFixed(0)}% of composite.`,
    inputs: 'Latest report per mapped type, or structured audit for evidence quality.',
    improve: `Generate ${types[0] ?? 'domain'} report when this pillar reads 0.00 or is missing.`,
  };
}
